// oracle.js — verifier coordinator (self-improve-hardening E1-WS1-T01).
// @arch:pure-engine
//
// THE LAW: the FINAL judge is an EXECUTABLE verifier (console error / type-check
// / lint / smoke / build). The Google model is TRIAGE ONLY (prioritize the fix),
// never the oracle of truth. The oracle has NO file-write surface — it only READS
// verifier results and asks the triage model for advisory text. (E1-WS3: the
// oracle is structurally confined; integrityGuard backstops the self-mod seams.)
//
// PURE coordination over INJECTABLE verifiers, so it's testable headlessly (no
// Electron/window). A verifier that is ABSENT is SKIPPED, never failed — honest:
// no false block when the live preview / a runner isn't available.

// OracleVerdict = { verifier: 'console'|'types'|'lint'|'smoke'|'build', passed: boolean, evidence: string }
export const VERIFIER_NAMES = ['console', 'types', 'lint', 'smoke', 'build'];

// Hard cap on in-turn auto-correction (the LAW constant): >2 repairs ≈ 0.97×
// baseline (net-negative). The loop nudges ≤ this many times; the next red ABORTs.
export const AUTO_CORRECT_CAP = 2;

const clip = (s, n = 2000) => String(s ?? '').slice(0, n);

/**
 * classifyOracleStep — the SINGLE decision the AUTO_CORRECT gate makes per oracle
 * evaluation. The LIVE loop (agentLoop) AND deterministic replay both call this,
 * so the outcome is one source of truth, never a drifting mirror. PURE.
 *   - noVerifiers (nothing ran)  → 'green'  (honest no-op; never a false block)
 *   - reachedGreen               → 'green'  (proceed to DONE)
 *   - attempts >= cap            → 'abort'  (the red after the cap → autocorrect_cap)
 *   - else                       → 'retry'  (nudge a fix; not yet at the cap)
 *
 * T01 (tier-s-sprint-unified-2026-06-28) — `unverified` signal: returns the
 * `unverified` boolean alongside the outcome so callers can distinguish
 *   - real green (verifiers ran and passed)
 *   - honest no-op green (no verifiers were available — TIER-S honest-green law)
 * Without this signal the UI badge "green" is misleading and indistinguishable
 * from a real verifier pass.
 * @returns {{ outcome: 'green'|'retry'|'abort', unverified: boolean }}
 */
export function classifyOracleStep({ reachedGreen = false, noVerifiers = false, attempts = 0, cap = AUTO_CORRECT_CAP } = {}) {
  if (noVerifiers) return { outcome: 'green', unverified: true };
  if (reachedGreen) return { outcome: 'green', unverified: false };
  if ((Number.isFinite(attempts) ? attempts : 0) >= cap) return { outcome: 'abort', unverified: false };
  return { outcome: 'retry', unverified: false };
}

/**
 * Run every PROVIDED verifier and aggregate OracleVerdict[]. A verifier is an
 * async `() => { passed, evidence }`; an absent name is skipped (not failed); a
 * verifier that THROWS counts as failed (it couldn't prove green) with the error
 * as evidence. Triage runs ONLY when there are failures AND a triage fn is given,
 * and is purely advisory — it NEVER flips a verdict. A triage failure/missing key
 * surfaces as `triageError` (VISIBLE, never swallowed — the "DEAD loop" lesson).
 *
 * @returns {{ verdicts: OracleVerdict[], failed: OracleVerdict[], reachedGreen: boolean,
 *             noVerifiers: boolean, triageAdvice: string|null, triageError: string|null }}
 */
export async function runOracle({ verifiers = {}, triage = null, taskText = '', signal } = {}) {
  const verdicts = [];
  for (const name of VERIFIER_NAMES) {
    const fn = verifiers && verifiers[name];
    if (typeof fn !== 'function') continue; // absent → skipped (not failed)
    let verdict;
    try {
      const r = await fn({ signal });
      verdict = { verifier: name, passed: r?.passed === true, evidence: clip(r?.evidence) };
    } catch (e) {
      verdict = { verifier: name, passed: false, evidence: `verifier error: ${e?.message || e}` };
    }
    verdicts.push(verdict);
  }
  const ran = verdicts.length;
  const failed = verdicts.filter((v) => !v.passed);
  const reachedGreen = ran > 0 && failed.length === 0;
  const noVerifiers = ran === 0;

  let triageAdvice = null;
  let triageError = null;
  if (failed.length > 0 && typeof triage === 'function') {
    try {
      const t = await triage({ verdicts: failed, taskText, signal });
      triageAdvice = t && t.advice ? clip(t.advice) : null;
      if (t && t.error) triageError = String(t.error);
    } catch (e) {
      triageError = e?.message || String(e); // explicit, never silent
    }
  }

  return { verdicts, failed, reachedGreen, noVerifiers, triageAdvice, triageError };
}

/**
 * Sprint tasking-cursor-ide P5-T03 — Per-task oracle wrapper.
 *
 * **LAW: this does NOT add a second judge.** It REUSES runOracle with the
 * exact same shape — we just stamp the result with `taskId` so the
 * checkpoint engine + Task Board can correlate verdicts to tasks. The
 * verifier set typically comes from the task's `acceptance` criterion
 * (resolved at the loop edge); when no verifier matched a task, the
 * verdict surfaces `noVerifiers:true` and the checkpoint engine treats
 * that as an honest no-op (no snapshot — see isHonestGreen).
 *
 * @param {object} opts
 * @param {string} opts.taskId — the current task in the run
 * @param {object} opts.verifiers — same shape runOracle accepts
 * @param {Function?} opts.triage
 * @param {string?} opts.taskText
 * @param {AbortSignal?} opts.signal
 * @returns {Promise<object>} the runOracle result + `{ taskId }` field
 */
export async function runOracleForTask(opts = {}) {
  const taskId = String(opts.taskId ?? '').trim();
  if (!taskId) throw new Error('runOracleForTask: taskId is required');
  const result = await runOracle({
    verifiers: opts.verifiers || {},
    triage: opts.triage || null,
    taskText: opts.taskText || '',
    signal: opts.signal,
  });
  return { ...result, taskId };
}

/**
 * Build the FIX nudge text from a red oracle result (consumed by the loop's
 * AUTO_CORRECT). Lists the failing verifiers + the (advisory) triage hint, and
 * makes the triage degradation VISIBLE if the key was missing.
 */
export function buildFixNudge(oracleResult) {
  const failed = Array.isArray(oracleResult?.failed) ? oracleResult.failed : [];
  const lines = failed.slice(0, 5).map((v) => `- [${v.verifier}] ${v.evidence || 'falhou'}`);
  const advice = oracleResult?.triageAdvice ? `Triagem (prioridade do fix): ${oracleResult.triageAdvice}` : '';
  const note = oracleResult?.triageError ? `(triagem indisponível: ${oracleResult.triageError})` : '';
  return [
    'Antes de concluir: a verificação automática (oráculo) reprovou. CORRIJA com apply_edit/write_file e confirme — não responda texto, aja:',
    ...lines,
    advice,
    note,
  ].filter(Boolean).join('\n');
}

/**
 * Factory: a `console` verifier from a live-preview bridge exposing `.console()`
 * (Electron `window.apolo.preview`). Returns { passed, evidence }: passed when the
 * console has zero error-level entries. A bridge error degrades to passed:true
 * (honest — a flaky bridge must not block DONE on a phantom failure). When the
 * bridge is absent the loop simply doesn't build this verifier (→ skipped).
 */
export function previewConsoleVerifier(previewApi) {
  return async () => {
    let entries = [];
    try {
      const res = await previewApi.console({ levels: ['error'], limit: 10 });
      if (res?.ok && Array.isArray(res.entries)) entries = res.entries;
    } catch {
      return { passed: true, evidence: 'preview console indisponível (não bloqueia)' };
    }
    if (entries.length === 0) return { passed: true, evidence: 'preview console limpo' };
    const evidence = entries
      .slice(0, 8)
      .map((e) => `[${String(e.level || 'error').toUpperCase()}] ${String(e.text || '').slice(0, 200)}`)
      .join('; ');
    return { passed: false, evidence };
  };
}
